/**
 * Example Scenarios Selector Component
 * Displays example scenarios for non-paying users to try.
 * Inputs stay editable while free runs remain; lock only after free runs are exhausted.
 */

import React, { useEffect, useState } from 'react';
import { SubscriptionManager } from '../SubscriptionManager.js';
import { exampleScenarios } from '../data/exampleScenarios.js';
import { smsfExampleScenarios } from '../data/smsfExampleScenarios.js';
import { trackAdvancedDemoShown } from '../utils/funnelAnalytics.js';

export const ExampleScenariosSelector = ({ 
  calculatorType = 'advanced',
  onLoadScenario,
  isPayingUser = false,
  scenarioIds = null,
}) => {
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get scenarios based on calculator type
  const allScenarios = calculatorType === 'smsf' 
    ? smsfExampleScenarios 
    : exampleScenarios;
  const scenarios = scenarioIds?.length
    ? allScenarios.filter((s) => scenarioIds.includes(s.id))
    : allScenarios;

  // Check if user has subscription
  const hasSubscription = isPayingUser || SubscriptionManager.hasAccess(calculatorType === 'smsf' ? 'smsf' : 'advanced');

  useEffect(() => {
    if (hasSubscription) return;
    trackAdvancedDemoShown({
      surface: 'example_selector',
      calculatorTier: calculatorType
    });
  }, [calculatorType, hasSubscription]);

  // If user has subscription, don't show this component
  if (hasSubscription) {
    return null;
  }

  const handleLoadScenario = async (scenario) => {
    setIsLoading(true);
    setSelectedScenario(scenario.id);

    // Track example scenario clicks in GA4 if available
    try {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'example_scenario_click', {
          calculator_type: calculatorType,
          calculator_tier: calculatorType,
          scenario_id: scenario.id,
          scenario_name: scenario.name,
          source: 'in_app_selector'
        });
      }
    } catch (e) {
      // Analytics errors should never break UX
      console.error('GA tracking error (example_scenario_click):', e);
    }

    try {
      if (onLoadScenario) {
        await onLoadScenario(scenario);
      }
    } catch (error) {
      console.error('Error loading scenario:', error);
      alert('Failed to load scenario. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
      <div className="mb-4">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Optional: preview with a sample case
        </h3>
        <p className="text-sm text-gray-600">
          Prefer to start with your own numbers. Or load a sample, then clear any field and run your plan.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {scenarios.map((scenario) => (
          <button
            key={scenario.id}
            onClick={() => handleLoadScenario(scenario)}
            disabled={isLoading}
            className={`
              relative bg-white border-2 rounded-lg p-4 text-left transition-all
              ${selectedScenario === scenario.id 
                ? 'border-blue-500 bg-blue-50 shadow-md' 
                : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
              }
              ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-semibold text-gray-900 text-base">
                {scenario.name}
              </h4>
              {selectedScenario === scenario.id && (
                <span className="text-blue-600 text-sm font-medium">✓ Loaded</span>
              )}
            </div>
            
            <p className="text-sm text-gray-600 mb-3">
              {scenario.description}
            </p>

            {scenario.highlights && scenario.highlights.length > 0 && (
              <ul className="text-xs text-gray-500 space-y-1">
                {scenario.highlights.slice(0, 3).map((highlight, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-blue-500 mr-1">•</span>
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>
            )}

            {isLoading && selectedScenario === scenario.id && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-lg">
                <div className="text-blue-600">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        {SubscriptionManager.canRunAdvancedFree().canRun ? (
          <p className="text-sm text-yellow-800">
            <strong>These are sample numbers.</strong> Clear any field and enter your own — your free run still works with your plan.
          </p>
        ) : (
          <p className="text-sm text-yellow-800">
            <strong>Want to test your plan instead?</strong> Unlock your own inputs, saved scenarios, and PDF reports.
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent('showPricingModal', {
                  detail: { source: 'advanced_demo', calculatorTier: calculatorType }
                }));
              }}
              className="text-blue-600 hover:text-blue-800 font-medium ml-1 underline"
            >
              Use your own numbers in Advanced
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default ExampleScenariosSelector;


